import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Btn, Field, Modal, Msg, inputCls, money } from '../components/ui'
import type { Service } from '../lib/db'

const CATEGORIES = ['production', 'post-production', 'drone', 'déplacement', 'autre']

/** Onglet Services & prix de la page Comptabilité : catalogue utilisé par l'éditeur de factures. */
export default function ServicesPanel() {
  const [list, setList] = useState<Service[]>([])
  const [form, setForm] = useState<Partial<Service> | null>(null)
  const [msg, setMsg] = useState('')

  const load = () => supabase.from('services').select('*').order('category').order('name').then(({ data }) => setList((data as any) || []))
  useEffect(() => { load() }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault(); if (!form) return
    const payload = { name: (form.name || '').trim(), description: form.description || null, default_price: Number(form.default_price) || 0, category: form.category || 'production', active: form.active !== false }
    if (!payload.name) { setMsg('Erreur : nom requis.'); return }
    const q = form.id ? supabase.from('services').update(payload).eq('id', form.id) : supabase.from('services').insert(payload)
    const { error } = await q
    if (error) { setMsg('Erreur : ' + error.message); return }
    setForm(null); setMsg('✅ Service enregistré.'); load()
  }
  async function toggle(s: Service) { await supabase.from('services').update({ active: !s.active }).eq('id', s.id); load() }
  async function remove(s: Service) {
    if (!confirm(`Supprimer « ${s.name} » ?`)) return
    const { error } = await supabase.from('services').delete().eq('id', s.id)
    if (error) setMsg('Erreur : ' + error.message + ' (désactivez-le plutôt s\'il est utilisé sur des factures)'); else load()
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4"><h2 className="font-display text-2xl tracking-wide">Services & prix</h2><Btn onClick={() => setForm({ category: 'production', active: true })}>+ Nouveau service</Btn></div>
      <p className="text-acier text-sm mb-4">Catalogue utilisé pour composer les factures en un clic. Les prix sont hors taxes.</p>
      <Msg msg={msg} />
      <div className="border border-white/10 rounded overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest"><tr><th className="text-left px-4 py-3">Service</th><th className="text-left px-4 py-3">Catégorie</th><th className="text-right px-4 py-3">Prix</th><th className="text-left px-4 py-3">Actif</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {list.map(s => (
              <tr key={s.id} className={`border-t border-white/5 hover:bg-white/[0.03] ${s.active ? '' : 'opacity-50'}`}>
                <td className="px-4 py-3"><button onClick={() => setForm(s)} className="font-medium hover:text-orange text-left">{s.name}</button>{s.description && <div className="text-xs text-acier">{s.description}</div>}</td>
                <td className="px-4 py-3 text-acier">{s.category}</td>
                <td className="px-4 py-3 text-right">{money(s.default_price)}</td>
                <td className="px-4 py-3"><button onClick={() => toggle(s)} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${s.active ? 'bg-green-500/15 text-green-300' : 'bg-white/10 text-acier'}`}>{s.active ? 'Actif' : 'Inactif'}</button></td>
                <td className="px-4 py-3 text-right text-xs uppercase tracking-widest"><button onClick={() => remove(s)} className="text-acier hover:text-red-300">Suppr.</button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-acier">Aucun service.</td></tr>}
          </tbody>
        </table>
      </div>
      {form && (
        <Modal title={form.id ? 'Modifier le service' : 'Nouveau service'} onClose={() => setForm(null)}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom *" className="sm:col-span-2"><input className={inputCls} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
            <Field label="Description" className="sm:col-span-2"><textarea className={inputCls} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Prix par défaut ($)"><input type="number" step="0.01" className={inputCls} value={form.default_price ?? ''} onChange={e => setForm({ ...form, default_price: +e.target.value })} /></Field>
            <Field label="Catégorie"><select className={inputCls} value={form.category || 'production'} onChange={e => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}{form.category && !CATEGORIES.includes(form.category) && <option>{form.category}</option>}</select></Field>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active !== false} onChange={e => setForm({ ...form, active: e.target.checked })} className="accent-orange" /> Actif (proposé dans les factures)</label>
            <div className="sm:col-span-2 flex gap-3"><Btn type="submit">Enregistrer</Btn><Btn type="button" variant="ghost" onClick={() => setForm(null)}>Annuler</Btn></div>
          </form>
        </Modal>
      )}
    </div>
  )
}
